import { Cards, Upgrades } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMErrorMessage,
  AEMUpgrades,
  Item,
  checkErrors,
  getEmptyContent,
  getImage,
  getLink,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { CustomerIssuedDocumentSummaries, EDocsAPIResponse } from '../models';

export const getUpgrades = (
  aem: AEMUpgrades | undefined,
  featureFlag: boolean | undefined,
  vouchersCount: string | undefined,
  eCredits: EDocsAPIResponse | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): Upgrades => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<Upgrades>(
        'upgrades',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      AEMUpgrades,
      [
        AEMUpgrades | undefined,
        string | undefined,
        EDocsAPIResponse | undefined,
        boolean | undefined
      ]
    >('title', aem, vouchersCount, eCredits, featureFlag)
      ? adaptUpgradesObject(
          aem as AEMUpgrades,
          vouchersCount as string,
          eCredits?.customerIssuedDocumentSummaryList
            ?.customerIssuedDocumentSummaries as CustomerIssuedDocumentSummaries[],
          featureFlag as boolean
        )
      : isDisplay<Upgrades>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'UPGRADES');
    return getEmptyContent<Upgrades>('upgrades', aemErrorMessages, true);
  }
};

const checkCount = (
  vouchersCount: string | undefined,
  eCreditsCount: string | undefined
): boolean => {
  return vouchersCount !== '0' || eCreditsCount !== '0';
};

const adaptUpgradesObject = (
  aem: AEMUpgrades,
  vouchersCount: string,
  eCredits: CustomerIssuedDocumentSummaries[] | undefined,
  featureFlag: boolean
): Upgrades => {
  const eCreditCount = getCreditCount(eCredits)?.toString();

  if (checkCount(vouchersCount, eCreditCount) && featureFlag) {
    return {
      title: aem.title,
      action: getLink(aem.link.label, aem.link.url),
      cards: getUpgradeCards(aem, vouchersCount, eCreditCount as string),
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<Upgrades>('', [], true, true);
  }
};

const getCreditCount = (
  eCredits: CustomerIssuedDocumentSummaries[] | undefined
) => {
  return (
    eCredits?.reduce((eCreditCount, eCredit) => {
      if (new Date() < new Date(eCredit.expirationDate)) {
        eCreditCount++;
      }
      return eCreditCount;
    }, 0) || 0
  );
};

const getUpgradeCards = (
  aem: AEMUpgrades,
  vouchersCount: string,
  eCreditsCount: string
): Cards[] => {
  const cards: Cards[] = [];

  aem.items.forEach((item: Item) => {
    const cardLabel: string = getCardLabel(item, vouchersCount, eCreditsCount);
    if (cardLabel !== '0') {
      const card: Cards = {
        image: getImage(item.image.src || '', item.image.alt || ''),
        link: getLink(item.redeemLink.label, item.redeemLink.url),
        label: cardLabel,
        title: item.title,
        type: item.type
      };
      cards.push(card);
    }
  });
  sortCards(cards);
  return cards;
};

const getCardLabel = (
  item: Item,
  vouchersCount: string,
  eCreditsCount: string
): string => {
  return item.type === '1'
    ? vouchersCount
    : item.type === '2'
      ? eCreditsCount
      : '';
};

const sortCards = (cards: Cards[]) => {
  cards.sort((leftCard, rightCard) => {
    const leftCardKey = leftCard.tag === undefined ? 0 : 1;
    const rightCardKey = rightCard.tag === undefined ? 0 : 2;
    return rightCardKey - leftCardKey;
  });
};
